/** Time / number helpers reused across cockpit pages. */

export function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

export function fmtPct(a: number, b: number, decimals = 1): string {
  if (b === 0) return a === 0 ? '0%' : 'n/a';
  return `${((a / b) * 100).toFixed(decimals)}%`;
}

export function deltaPct(a: number, b: number): number {
  if (b === 0) return a === 0 ? 0 : 100;
  return Number(((a - b) / b * 100).toFixed(0));
}

export function agoLabel(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 60) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return 'none';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return 'none';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function fmtBytes(n: number | null | undefined): string {
  if (!n) return '0 B';
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(1)} GB`;
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

export const shortId = (id: string | null | undefined) => (id ? id.slice(0, 8) : 'none');

/** Build an array of {day, count} for the last N days, filled in from raw timestamp rows. */
export function bucketByDay(
  rows: { created_at: string }[] | { opened_at: string }[] | any[],
  days: number,
  tsField: string = 'created_at',
): { day: string; count: number }[] {
  const buckets: Record<string, number> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86_400_000);
    buckets[d.toISOString().slice(0, 10)] = 0;
  }
  for (const r of rows) {
    const key = (r as any)[tsField]?.slice?.(0, 10);
    if (key && buckets[key] !== undefined) buckets[key] += 1;
  }
  return Object.entries(buckets).map(([day, count]) => ({ day, count }));
}

/** A YYYY-MM-DD London day as "29 Oct". */
export function fmtDay(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Percentage change, rounded; null when there is no base. */
export function change(now: number, before: number): number | null {
  if (!before) return null;
  return Math.round(((now - before) / before) * 100);
}
